/**
 * Tokens for the feedback hub (production's API), so a quick note can be sent from staging, a preview or production
 * itself (feedback quick capture spec 01 §3).
 *
 * Where the app already signs in through MSAL (staging, production) the hub token is a second scope on that same
 * instance: silent when the tenant has consented, a popup when it has not. On a mock preview there is no MSAL
 * instance, so one is made the first time a note is sent and the person signs in by popup; nothing else in a mock
 * preview changes. Reached only from the data layer's token provider, so nothing here runs until Send.
 *
 * `msalConfig` reads configuration at import, so it is imported dynamically, after boot.
 */
import type { IPublicClientApplication } from "@azure/msal-browser";
import { setFeedbackHubTokenProvider } from "@shared-core/data/api/providers";

let appInstance: IPublicClientApplication | null = null;
let lazyInstance: Promise<IPublicClientApplication> | null = null;

/** Hand over the MSAL instance the app already signs in with, so the hub token is acquired on it. */
export function shareMsalWithFeedbackHub(
  instance: IPublicClientApplication,
): void {
  appInstance = instance;
}

function msalForHub(): Promise<IPublicClientApplication> {
  if (appInstance) return Promise.resolve(appInstance);
  lazyInstance ??= (async () => {
    const [{ PublicClientApplication }, { getMsalConfig, msalClientId }] =
      await Promise.all([
        import("@azure/msal-browser"),
        import("./entra/msalConfig"),
      ]);
    if (!msalClientId.trim()) {
      throw new Error(
        "Sign-in for feedback is not set up in this environment (no MSAL client id).",
      );
    }
    const instance = new PublicClientApplication(getMsalConfig());
    await instance.initialize();
    return instance;
  })();
  // A failed set-up is tried again on the next Send, not remembered.
  lazyInstance.catch(() => {
    lazyInstance = null;
  });
  return lazyInstance;
}

async function acquireHubToken(scope: string): Promise<string> {
  const [{ InteractionRequiredAuthError, BrowserAuthErrorCodes }, msal] =
    await Promise.all([import("@azure/msal-browser"), msalForHub()]);
  const { silentRedirectUri } = await import("./entra/msalConfig");
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  if (account) {
    try {
      const silent = await msal.acquireTokenSilent({
        scopes: [scope],
        account,
        redirectUri: silentRedirectUri,
      });
      return silent.accessToken;
    } catch (err) {
      if (!(err instanceof InteractionRequiredAuthError)) throw err;
    }
  }
  try {
    const popup = await msal.acquireTokenPopup({
      scopes: [scope],
      ...(account ? { account } : {}),
    });
    msal.setActiveAccount(popup.account);
    return popup.accessToken;
  } catch (err) {
    throw popupFailure(err, BrowserAuthErrorCodes);
  }
}

/** What a failed sign-in popup tells the person, in words; any other failure is passed on as it is. */
function popupFailure(
  err: unknown,
  codes: { userCancelled: string; popupWindowError: string },
): unknown {
  const code = (err as { errorCode?: string }).errorCode;
  if (code === codes.userCancelled) {
    return new Error("Sign-in was cancelled. Send again to sign in.");
  }
  if (code === codes.popupWindowError) {
    return new Error(
      "The sign-in window was blocked. Allow pop-ups for this site and send again.",
    );
  }
  return err;
}

/** Register the hub token provider. Call once at boot, only when the runtime config names a hub scope. */
export function installFeedbackHubTokenProvider(scope: string): void {
  // Two calls at once (an image upload and the note) share one sign-in, never two popups.
  let inFlight: Promise<string> | null = null;
  setFeedbackHubTokenProvider(() => {
    inFlight ??= acquireHubToken(scope).finally(() => {
      inFlight = null;
    });
    return inFlight;
  });
}
